import { t } from "i18next";
import { Label } from "@radix-ui/react-label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useState } from "react";
import { Input } from "../../ui/input";
import { Button } from "../../ui/button";
import { X } from "lucide-react";
import { useController } from "react-hook-form";
import xor from "lodash/xor";

const HEADINGS: string[] = [
  "Physical Exam",
  "Medications",
  "Family History",
  "Allergies",
];

export type HeadingEditorProps = {
  name: string;
};

export const HeadingEditor = ({ name }: HeadingEditorProps): JSX.Element => {
  const [isCreatingNewHeading, setIsCreatingNewHeading] =
    useState<boolean>(false);
  const { field: selectedHeadingsField } = useController({
    name,
    shouldUnregister: true,
  });
  const { field: newHeadingField } = useController({
    name: name + "New",
    shouldUnregister: true,
  });
  const selectedHeadings = selectedHeadingsField.value as string[];

  const handleSelectChange = (value: string): void => {
    selectedHeadingsField.onChange(xor(selectedHeadings, [value]));
  };

  const handleAddNewHeading = (
    event: React.MouseEvent<HTMLButtonElement>
  ): void => {
    event.preventDefault();
    handleSelectChange(newHeadingField.value as string);
    newHeadingField.onChange("");
    setIsCreatingNewHeading(false);
  };

  const handleRemoveHeading = (
    event: React.MouseEvent<HTMLButtonElement>,
    heading: string
  ): void => {
    event.preventDefault();
    handleSelectChange(heading);
  };

  return (
    <div className="space-y-2">
      <Label htmlFor="headings">
        {t("templates.createTemplateForm.selectHeadingsLabel")}
      </Label>
      <Select onValueChange={handleSelectChange}>
        <SelectTrigger id="headings">
          <SelectValue
            placeholder={t(
              "templates.createTemplateForm.selectHeadingsPlaceholder"
            )}
          />
        </SelectTrigger>
        <SelectContent>
          {HEADINGS.map((heading) => (
            <SelectItem key={heading} value={heading}>
              {heading}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {selectedHeadings.map((heading, index) => (
        <div
          key={index}
          className="inline-flex items-center bg-grey-100 rounded px-2 py-1 text-sm font-medium mr-2 mt-1"
        >
          <span>{heading}</span>
          <button
            onClick={(event) => {
              handleRemoveHeading(event, heading);
            }}
          >
            <X className="ml-2 size-4" />
          </button>
        </div>
      ))}
      <span
        className="text-xs text-grey-300 hover:text-grey-400 cursor-pointer"
        onClick={() => {
          setIsCreatingNewHeading(true);
        }}
      >
        {t("templates.createTemplateForm.addNewHeadingLink")}
      </span>
      {isCreatingNewHeading && (
        <>
          <Input
            className="h-8"
            name="new_heading"
            type="text"
            placeholder={t(
              "templates.createTemplateForm.addNewHeadingPlaceholder"
            )}
            onChange={(event) => {
              newHeadingField.onChange(event.target.value);
            }}
          />
          <Button size="sm" type="button" onClick={handleAddNewHeading}>
            {t("templates.createTemplateForm.addNewHeadingButton")}
          </Button>
        </>
      )}
    </div>
  );
};
